import React, { useEffect, useState } from "react";
import { CalendarClock, Play, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, extractErrorMessage } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—");

function JobRow({ j, onRun, busy }) {
  const last = j.last;
  const running = last?.durum === "calisiyor";
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${j.overdue ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white"}`} data-testid={`job-${j.key}`}>
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
          {j.label}
          <span className="text-[11px] font-normal text-slate-500">{j.schedule}</span>
          {j.overdue && <span className="text-[10px] font-bold bg-rose-600 text-white rounded px-1.5 py-0.5" data-testid={`job-overdue-${j.key}`}>Gecikti</span>}
        </div>
        <div className="text-xs text-slate-600 mt-1 grid sm:grid-cols-2 gap-x-4">
          <span>Son zamanlanmış çalışma: <b data-testid={`job-last-scheduled-${j.key}`}>{fmt(j.last_scheduled_at)}</b></span>
          <span>Son başarılı: <b data-testid={`job-last-ok-${j.key}`}>{fmt(j.last_ok?.at)}</b>{j.last_ok?.trigger === "elle" ? " (elle)" : ""}</span>
        </div>
        {last && (
          <div className={`text-[11px] mt-1 inline-flex items-center gap-1 ${last.durum === "hata" ? "text-rose-700" : running ? "text-sky-700" : "text-emerald-700"}`} data-testid={`job-last-status-${j.key}`}>
            {running ? <Loader2 size={11} className="animate-spin" /> : last.durum === "hata" ? <AlertTriangle size={11} /> : <CheckCircle2 size={11} />}
            Son çalışma {fmt(last.at)} · {running ? "çalışıyor" : last.durum === "hata" ? `hata: ${last.hata || ""}` : "başarılı"}
          </div>
        )}
      </div>
      <button type="button" onClick={() => onRun(j.key)} disabled={busy || running} data-testid={`job-run-${j.key}`}
        className="shrink-0 inline-flex items-center gap-1 text-xs font-bold bg-slate-900 hover:bg-slate-700 text-white px-2.5 py-1.5 rounded-lg disabled:opacity-50">
        <Play size={12} /> Şimdi çalıştır
      </button>
    </div>
  );
}

export default function ScheduledJobsPanel() {
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/scheduled-jobs").then((r) => setJobs(r.data?.jobs || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async (key) => {
    setBusy(key);
    try {
      await api.post(`/admin/scheduled-jobs/${key}/run`);
      toast.success("Görev başlatıldı");
      setTimeout(load, 800);
      setTimeout(load, 6000);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Görev başlatılamadı"));
    } finally { setBusy(null); }
  };

  const overdue = jobs.filter((j) => j.overdue).length;
  return (
    <div className="border border-slate-200 rounded-xl p-4" data-testid="scheduled-jobs-panel">
      <h4 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-1">
        <CalendarClock size={16} /> Zamanlanmış Görevler
      </h4>
      <p className="text-xs text-slate-500 mb-3">
        Görevlerin en son ne zaman otomatik çalıştığı. "Gecikti" etiketi, görevin planlanan zamanda tetiklenmediğini gösterir
        {overdue > 0 && <b className="text-rose-700"> · {overdue} görev gecikmede</b>}.
      </p>
      <div className="space-y-2">
        {jobs.map((j) => <JobRow key={j.key} j={j} onRun={run} busy={busy === j.key} />)}
      </div>
    </div>
  );
}
